/**
 * to keep the code clean and organized, we will export all models from this file.
 * This allows us to import them easily in other parts of the application.
 * We will also define an enum for model names and a function to get models by name.
 * This file serves as a central point for managing models in the application.
 *
 * 🙏 please keep models ordered alphabetically by their file names.
 */
import AuthenticationLog from './AuthenticationLog.js';
import Brand from './Brand.js';
import Deposit from './Deposit.js';
import Equipment from './Equipment.js';
import EquipmentSize from './EquipmentSize.js';
import History from './History.js';
import InventoryEquipment from './InventoryEquipment.js';
import Location from './Location.js';
import MaintenanceRecord from './MaintenanceRecord.js';
import Order from './Order.js';
import Organization from './Organization.js';
import OrderItem from './OrderItem.js';
import Park from './Park.js';
import Payment from './Payment.js';
import Product from './Product.js';
import ProductTest from './ProductTest.js';
import ProductType from './ProductType.js';
import ProductReview from './ProductReview.js';
import Registration from './Registration.js';
import Tour from './Tour.js';
import TourExponent from './TourExponent.js';

// Global models export
export {
  AuthenticationLog,
  Brand,
  Deposit,
  Equipment,
  EquipmentSize,
  History,
  InventoryEquipment,
  Location,
  MaintenanceRecord,
  Order,
  Organization,
  OrderItem,
  Park,
  Payment,
  Product,
  ProductTest,
  ProductType,
  ProductReview,
  Registration,
  Tour,
  TourExponent,
}

// Enum for model names
export enum ModelName {
  AUTHENTICATION_LOG = 'AuthenticationLog',
  BRAND = 'Brand',
  DEPOSIT = 'Deposit',
  EQUIPMENT = 'Equipment',
  EQUIPMENT_SIZE = 'EquipmentSize',
  HISTORY = 'History',
  INVENTORY_EQUIPMENT = 'InventoryEquipment',
  LOCATION = 'Location',
  MAINTENANCE_RECORD = 'MaintenanceRecord',
  ORDER = 'Order',
  ORGANIZATION = 'Organization',
  ORDER_ITEM = 'OrderItem',
  PARK = 'Park',
  PAYMENT = 'Payment',
  PRODUCT = 'Product',
  PRODUCT_TEST = 'ProductTest',
  PRODUCT_TYPE = 'ProductType',
  PRODUCT_REVIEW = 'ProductReview',
  REGISTRATION = 'Registration',
  TOUR = 'Tour',
  TOUR_EXPONENT = 'TourExponent',
}


// Generic model type definitions
export type ModelType =
  | typeof AuthenticationLog
  | typeof Brand
  | typeof Deposit
  | typeof Equipment
  | typeof EquipmentSize
  | typeof History
  | typeof InventoryEquipment
  | typeof Location
  | typeof MaintenanceRecord
  | typeof Order
  | typeof Organization
  | typeof OrderItem
  | typeof Park
  | typeof Payment
  | typeof Product
  | typeof ProductTest
  | typeof ProductType
  | typeof ProductReview
  | typeof Registration
  | typeof Tour
  | typeof TourExponent
  ;

// Mapping between ModelName and ModelType
const modelMapping: Record<ModelName, ModelType> = {
  [ModelName.AUTHENTICATION_LOG]: AuthenticationLog,
  [ModelName.BRAND]: Brand,
  [ModelName.DEPOSIT]: Deposit,
  [ModelName.EQUIPMENT]: Equipment,
  [ModelName.EQUIPMENT_SIZE]: EquipmentSize,
  [ModelName.HISTORY]: History,
  [ModelName.INVENTORY_EQUIPMENT]: InventoryEquipment,
  [ModelName.LOCATION]: Location,
  [ModelName.MAINTENANCE_RECORD]: MaintenanceRecord,
  [ModelName.ORDER]: Order,
  [ModelName.ORGANIZATION]: Organization,
  [ModelName.ORDER_ITEM]: OrderItem,
  [ModelName.PARK]: Park,
  [ModelName.PAYMENT]: Payment,
  [ModelName.PRODUCT]: Product,
  [ModelName.PRODUCT_TEST]: ProductTest,
  [ModelName.PRODUCT_TYPE]: ProductType,
  [ModelName.PRODUCT_REVIEW]: ProductReview,
  [ModelName.REGISTRATION]: Registration,
  [ModelName.TOUR]: Tour,
  [ModelName.TOUR_EXPONENT]: TourExponent,
};

// UI display names for models
export const uiDisplayNames: Record<string, string> = {
  AuthenticationLog: 'Authentication log',
  Brand: 'Brand',
  Deposit: 'Deposit',
  Equipment: 'Item',
  EquipmentSize: 'Item size',
  History: 'History',
  InventoryEquipment: 'Inventory item',
  Location: 'Location',
  MaintenanceRecord: 'Maintenance record',
  Order: 'Order',
  Organization: 'Organization',
  OrderItem: 'Order item',
  Park: 'Park',
  Payment: 'Payment',
  Product: 'Model',
  ProductTest: 'Product test',
  ProductType: 'Model category',
  ProductReview: 'Product review',
  Registration: 'Registration',
  Tour: 'Event',
  TourExponent: 'Exhibitor',
};

// Get a model by its name.
export function getModelByName(name: ModelName): ModelType {
  if (!(name in modelMapping)) {
    throw new Error(`Model not found`);
  }
  return modelMapping[name];
}

// alternative function to get a model by its string representation
export function getModelByString(name: string): ModelType {
  if (Object.values(ModelName).includes(name as ModelName)) {
    return modelMapping[name as ModelName];
  }
  throw new Error(`Model name ${name} not found`);
}
